<template>
  <div class="teleprompter" :class="{ '-recording': isRecording }">
    <div class="teleprompter-viewport" ref="viewport">
      <div
        class="teleprompter-content"
        ref="content"
        :style="{ transform: `translateY(${-scrollPosition}px)` }"
      >
        <div class="text-content" dir="auto" v-html="formattedScript" />
      </div>
      <div class="current-line-indicator" />
      <div class="gradient-overlay gradient-top" />
      <div class="gradient-overlay gradient-bottom" />
    </div>
    <!-- <div class="progress-bar">
      <div class="progress-fill" :style="{ width: `${progress}%` }" />
    </div> -->
    <div class="teleprompter-controls" v-if="isRecording">
      <div class="speed-control">
        <label>Speed</label>
        <button
          @click="decreaseSpeed"
          :disabled="scrollSpeed <= 0.5"
          title="Decrease Speed"
        >
          -
        </button>
        <span>{{ scrollSpeed }}x</span>
        <button
          @click="increaseSpeed"
          :disabled="scrollSpeed >= 5"
          title="Increase Speed"
        >
          +
        </button>
      </div>
      <button @click="togglePause" title="Pause Teleprompter">
        {{ isPaused ? '⏵' : '⏸' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Teleprompter',

  props: {
    script: {
      type: String,
      required: true,
    },
    isRecording: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      progress: 0,
      scrollPosition: 0,
      scrollSpeed: 1,
      isPaused: false,
      animationFrameId: null,
      lastTimestamp: null,
    };
  },

  mounted() {
    const savedSpeed = window.localStorage.getItem('teleprompterSpeed');
    if (savedSpeed) {
      this.scrollSpeed = parseFloat(savedSpeed);
    }
  },

  watch: {
    scrollSpeed(newVal) {
      window.localStorage.setItem('teleprompterSpeed', newVal);
    },
    isRecording: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.startScrolling();
        } else {
          this.stopScrolling();
        }
      },
    },
  },

  computed: {
    formattedScript() {
      if (!this.script) return '';

      // Split by new lines first to preserve paragraph structure
      const paragraphs = this.script.split('\n');

      return paragraphs
        .map((paragraph) => {
          if (!paragraph.trim()) return '<p class="empty-paragraph"></p>';

          // Split into sentences first
          const sentences = paragraph
            .replace(/([.!?:])\s+/g, '$1\n')
            .split('\n')
            .filter((sentence) => sentence.trim());

          // Process each sentence into word groups
          const formattedContent = sentences
            .map((sentence) => {
              const words = sentence.split(/\s+/);
              const lines = [];
              let currentLine = [];

              words.forEach((word) => {
                currentLine.push(word);

                // If we have 4-6 words or it's the last word of the sentence
                if (currentLine.length >= 4 && currentLine.length <= 6) {
                  lines.push(currentLine.join(' '));
                  currentLine = [];
                } else if (currentLine.length > 6) {
                  // If we exceeded 6 words, create a line with the first 6
                  const lineWords = currentLine.slice(0, 6);
                  lines.push(lineWords.join(' '));
                  currentLine = currentLine.slice(6);
                }
              });

              // Handle any remaining words
              if (currentLine.length > 0) {
                // If we have 1-3 words remaining, try to redistribute with previous line
                if (currentLine.length < 4 && lines.length > 0) {
                  const lastLine = lines.pop().split(' ');
                  const combinedWords = [...lastLine, ...currentLine];

                  // If combined length is 6 or less, make it one line
                  if (combinedWords.length <= 6) {
                    lines.push(combinedWords.join(' '));
                  } else {
                    // Otherwise, split optimally
                    const midPoint = Math.ceil(combinedWords.length / 2);
                    lines.push(combinedWords.slice(0, midPoint).join(' '));
                    lines.push(combinedWords.slice(midPoint).join(' '));
                  }
                } else {
                  lines.push(currentLine.join(' '));
                }
              }

              return lines.join('<br>');
            })
            .join('<br>');

          return `<p>${formattedContent}</p>`;
        })
        .join(''); // No need for newlines between paragraphs anymore
    },
  },

  methods: {
    startScrolling() {
      this.isPaused = false;
      this.scrollPosition = 0;
      this.lastTimestamp = null;
      // Request the first animation frame
      this.animationFrameId = requestAnimationFrame(this.animate);
    },

    animate(timestamp) {
      if (this.isPaused) {
        this.lastTimestamp = null;
        return;
      }

      if (!this.lastTimestamp) {
        this.lastTimestamp = timestamp;
      }

      const delta = timestamp - this.lastTimestamp;
      const pixelsPerSecond = 45 * this.scrollSpeed;
      const pixelsToMove = (delta / 1000) * pixelsPerSecond;

      const content = this.$refs.content;
      const viewport = this.$refs.viewport;

      if (content && viewport) {
        const maxScroll = content.offsetHeight - viewport.offsetHeight;

        if (this.scrollPosition < maxScroll) {
          this.scrollPosition = Math.min(
            this.scrollPosition + pixelsToMove,
            maxScroll
          );
          this.progress = Math.round((this.scrollPosition / maxScroll) * 100); // Add this line
          this.lastTimestamp = timestamp;
          // Request the next animation frame with requestAnimationFrame
          this.animationFrameId = requestAnimationFrame(this.animate);
        }
      } else {
        // If refs aren't available yet, keep trying
        this.animationFrameId = requestAnimationFrame(this.animate);
      }
    },

    togglePause() {
      this.isPaused = !this.isPaused;
      if (!this.isPaused) {
        this.lastTimestamp = null;
        // Restart animation when unpausing
        this.animationFrameId = requestAnimationFrame(this.animate);
      }
    },

    stopScrolling() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
      }
      this.scrollPosition = 0;
      this.isPaused = true;
    },

    increaseSpeed() {
      this.scrollSpeed = Math.min(this.scrollSpeed + 0.25, 5);
    },

    decreaseSpeed() {
      this.scrollSpeed = Math.max(this.scrollSpeed - 0.25, 0.5);
    },
  },

  beforeDestroy() {
    this.stopScrolling();
  },
};
</script>

<style lang="scss" scoped>
.teleprompter {
  --teleprompter-height: 268px;
  position: relative;
  width: 100%;
  height: var(--teleprompter-height);

  .teleprompter-viewport {
    position: relative;
    height: 100%;
    overflow: hidden;
    padding: 20px 30px;
  }

  .teleprompter-content {
    transition: transform 0.1s linear;
    padding-top: calc(var(--teleprompter-height) / 2);
    padding-bottom: calc(var(--teleprompter-height) / 2);
  }

  .text-content {
    font-size: 38px;
    line-height: 1.5;
    white-space: pre-wrap;
    /deep/ .empty-paragraph {
      height: 0.25em;
    }
  }

  .current-line-indicator {
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 2px; /* Reduced from 40px to a thin line */
    transform: translateY(-50%);
    // background: rgba(
    //   6,
    //   169,
    //   244,
    //   0.75
    // ); /* Bright orange-red, common in professional teleprompters */
    pointer-events: none;
    z-index: -10; /* Increased from -10 to appear above text */

    /* Left arrow marker */
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-130%);
      width: 0;
      height: 0;
      border-top: 10px solid transparent;
      border-bottom: 10px solid transparent;
      border-left: 15px solid rgba(0, 0, 0, 0.75);
    }

    /* Right arrow marker */
    // &::after {
    //   content: '';
    //   position: absolute;
    //   right: 0;
    //   top: 50%;
    //   transform: translateY(-50%);
    //   width: 0;
    //   height: 0;
    //   border-top: 10px solid transparent;
    //   border-bottom: 10px solid transparent;
    //   border-right: 15px solid rgba(6, 169, 244, 1);
    // }

    /* Optional highlight area around the line */
    // &::before {
    //   content: '';
    //   position: absolute;
    //   left: 15px; /* Start after the arrow */
    //   right: 15px;
    //   height: 40px; /* Height of highlight area */
    //   top: -20px; /* Center vertically */
    //   background: rgba(255, 87, 34, 0.1); /* Very subtle highlight */
    //   z-index: -1;
    // }
  }

  .gradient-overlay {
    position: absolute;
    left: 0;
    right: 0;
    height: 10px;
    pointer-events: none;
    z-index: 10;

    &.gradient-top {
      top: 0;
      background: linear-gradient(
        to bottom,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 0.9) 0.5%,
        rgba(255, 255, 255, 0) 100%
      );
    }

    &.gradient-bottom {
      bottom: 0;
      background: linear-gradient(
        to top,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 0.9) 0.5%,
        rgba(255, 255, 255, 0) 100%
      );
    }
  }

  .progress-bar {
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 100%;
    z-index: 10;
    height: 2px;
    background: rgba(0, 0, 0, 0);
    z-index: 10;
  }

  .progress-fill {
    height: 100%;
    background: #06a9f4; // You can adjust this color to match your theme
    transition: width 1s;
  }

  .teleprompter-controls {
    z-index: 10;
    position: absolute;
    bottom: 0;
    // transform: translateY(50%);
    right: 0;
    display: flex;
    gap: 10px;
    align-items: center;
    // background: rgba(255, 255, 255, 0.9);
    padding: 3px;
    border-radius: 4px;
    // box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);

    .speed-control {
      display: flex;
      align-items: center;
      gap: 4px;

      label {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.75);
        font-weight: 500;
      }

      button {
        width: 20px;
        height: 20px;
        min-width: 20px;
        padding: 0;
        border: 1px solid #d9d9d9;
        border-radius: 3px;
        background: white;
        cursor: pointer;
        font-size: 14px;
        line-height: 1;

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }

      span {
        font-size: 12px;
        min-width: 20px;
        text-align: center;
        font-weight: 400;
      }
    }

    button {
      height: 20px;
      padding: 0 8px;
      border: 1px solid #d9d9d9;
      border-radius: 3px;
      background: white;
      cursor: pointer;
      font-size: 14px;

      &:hover:not(:disabled) {
        background: #f5f5f5;
      }
    }
  }
}
</style>
